import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import {
  getProvisionedJobRole,
  getProvisionedJobRoleVersions,
} from 'http/hyperAdminProvisioned';
import { routesAliases, buildProvisionedRoleEditPath } from 'router/routesAliases';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JobTaskVersionRequirementsEditor from 'pages/Tasks/Edit/JobTaskVersionRequirementsEditor';

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(4, 5),
  },
}));

const EditRoleVersionRequirements = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const { companyId, roleId, versionId } = routerMatch.params;
  const numericCompanyId = Number(companyId);
  const [notFoundError, setNotFoundError] = useState(false);
  const [jobTask, setJobTask] = useState(null);
  const [version, setVersion] = useState(null);
  const [allVersions, setAllVersions] = useState([]);

  const backUrl = buildProvisionedRoleEditPath(companyId, roleId);

  const [fetchState, fetchData] = useAsyncFn(async () => {
    const jobTaskResult = await getProvisionedJobRole(numericCompanyId, roleId);

    if (jobTaskResult?.error) {
      if (jobTaskResult.statusCode === 404) {
        setNotFoundError(true);
      }
      return { jobTaskResult };
    }

    setJobTask(jobTaskResult);

    const versionsResult = await getProvisionedJobRoleVersions(numericCompanyId, roleId, {
      page: 0,
      pageSize: 100,
    });

    if (versionsResult?.error || versionsResult?.statusCode) {
      return { jobTaskResult, versionsResult };
    }

    const items = versionsResult.items || [];
    setAllVersions(items);
    const foundVersion = items.find(item => item.id === Number(versionId));
    if (!foundVersion) {
      setNotFoundError(true);
      return { jobTaskResult, versionsResult };
    }

    setVersion(foundVersion);
    setNotFoundError(false);
    return { jobTaskResult, versionsResult };
  }, [numericCompanyId, roleId, versionId]);

  useEffect(() => {
    setNotFoundError(false);
    setJobTask(null);
    setVersion(null);
    setAllVersions([]);
    fetchData();
  }, [companyId, roleId, versionId, fetchData]);

  const breadCrumbsItems = useMemo(
    () => [
      {
        title: t(routeProps.parentTitle),
        link: routeProps.parentPath,
      },
      {
        title: t('routes.editCompany'),
        link: `${routesAliases.editCompany}/${companyId}`,
      },
      {
        title: jobTask?.name || t('routes.editJobTask'),
        link: backUrl,
      },
      {
        title: t('routes.jobTaskVersionRequirements'),
        isActive: true,
      },
    ],
    [backUrl, companyId, jobTask?.name, routeProps.parentPath, routeProps.parentTitle, t],
  );

  const handleBack = () =>
    history.push({
      pathname: backUrl,
      state: location.state,
    });

  if (notFoundError) {
    return <NotFound />;
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || !version}
    >
      {version && (
        <JobTaskVersionRequirementsEditor
          version={version}
          allVersions={allVersions}
          onCancel={handleBack}
          layout="page"
          apiMode="provisioned"
          jobRoleId={Number(roleId)}
          provisionedCompanyId={numericCompanyId}
          isReadOnly={false}
        />
      )}
    </PageLayout>
  );
};

EditRoleVersionRequirements.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default EditRoleVersionRequirements;
